import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useAtom, useAtomValue } from 'jotai'
import { AnimatePresence, motion, type Transition, type Variants } from 'framer-motion'
import { Flag, Maximize2, X } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { Button, Interactive, Tooltip } from '@highlight/ui'
import { activeMissionAtom, findMission, NO_MISSION, trackTour, type TourSurface } from './tour-state'

const COMPLETE_HOLD_MS = 2500
/** Long enough for a settings tab or view to load its list before an optional step counts as absent. */
const OPTIONAL_STEP_WAIT_MS = 4000
const MAX_SCROLL_ATTEMPTS = 3
const SCROLL_RETRY_MS = 1000
const SPOTLIGHT_PADDING = 6
const SPOTLIGHT_RADIUS = 16
const CARD_WIDTH = 300
const CARD_GAP = 14
/** The meeting tour's card surface, so both tours read as one pattern. */
const CARD_SURFACE = 'bg-background-overlay corner-smooth shadow-lg'

const CARD_VARIANTS: Variants = {
  hidden: { opacity: 0, y: 12, filter: 'blur(4px)' },
  shown: { opacity: 1, y: 0, filter: 'blur(0px)' },
}
const CARD_TRANSITION: Transition = { type: 'spring', bounce: 0, duration: 0.4 }

interface Box {
  left: number
  top: number
  right: number
  bottom: number
}

const targetSelector = (target: string) => `[data-tour-section="${target}"]`

interface TargetBoxes {
  ring: Box
  /** Kept clear of the card. For a menu item this is the whole open menu. */
  avoid: Box
  /** Its center is on screen and not under something else, like a modal or a scroller's edge. */
  inSight: boolean
}

const toBox = (rect: DOMRect): Box => ({ left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom })

/** The window with a rounded hole over the target, cut by the even-odd rule. */
function spotlightPath(box: Box): string {
  const left = box.left - SPOTLIGHT_PADDING
  const top = box.top - SPOTLIGHT_PADDING
  const right = box.right + SPOTLIGHT_PADDING
  const bottom = box.bottom + SPOTLIGHT_PADDING
  const r = Math.min(SPOTLIGHT_RADIUS, (right - left) / 2, (bottom - top) / 2)
  const hole = `M${left + r} ${top}H${right - r}A${r} ${r} 0 0 1 ${right} ${top + r}V${bottom - r}A${r} ${r} 0 0 1 ${right - r} ${bottom}H${left + r}A${r} ${r} 0 0 1 ${left} ${bottom - r}V${top + r}A${r} ${r} 0 0 1 ${left + r} ${top}Z`
  return `path(evenodd, "M0 0H${window.innerWidth}V${window.innerHeight}H0Z${hole}")`
}

/** Polls every frame because targets mount late, like a portaled menu item, and move with scroll or panel drags. */
function useTargetBoxes(target: string | null): TargetBoxes | null {
  // Keyed by target: on the render after a step changes, the last reading still belongs to the previous target.
  const [reading, setReading] = useState<{ target: string; boxes: TargetBoxes | null } | null>(null)
  useEffect(() => {
    if (!target) {
      return
    }
    let frame = 0
    // Null so a new target's first reading always lands; '' would match "not found" and keep the old step's box.
    let last: string | null = null
    const tick = () => {
      const element = document.querySelector(targetSelector(target))
      const rect = element?.getBoundingClientRect()
      const visible = rect !== undefined && rect.width > 0 && rect.height > 0
      const menu = visible ? element?.closest('[role="menu"]')?.getBoundingClientRect() : undefined
      const avoid = menu ?? rect
      const hit = visible ? document.elementFromPoint((rect.left + rect.right) / 2, (rect.top + rect.bottom) / 2) : null
      const inSight = hit !== null && (!!element?.contains(hit) || hit.closest('[data-tour-card]') !== null)
      const key =
        visible && avoid ? `${rect.left},${rect.top},${rect.right},${rect.bottom},${avoid.bottom},${inSight}` : ''
      if (key !== last) {
        last = key
        setReading({ target, boxes: visible && avoid ? { ring: toBox(rect), avoid: toBox(avoid), inSight } : null })
      }
      frame = requestAnimationFrame(tick)
    }
    tick()
    return () => cancelAnimationFrame(frame)
  }, [target])
  return reading && reading.target === target ? reading.boxes : null
}

/** Index of the last listed target on screen in this window, or -1; skip-ahead jumps to it. */
function useFurthestShowing(targets: Array<string | null>): number {
  const [index, setIndex] = useState(-1)
  const key = targets.join('|')
  useEffect(() => {
    const list = key ? key.split('|') : []
    if (list.every((target) => !target)) {
      setIndex(-1)
      return
    }
    let frame = 0
    const tick = () => {
      let found = -1
      // A hidden window keeps its DOM; only a page the user can see counts as being there.
      if (document.visibilityState === 'visible')
        list.forEach((target, i) => {
          const rect = target ? document.querySelector(targetSelector(target))?.getBoundingClientRect() : undefined
          if (rect && rect.width > 0 && rect.height > 0) found = i
        })
      setIndex(found)
      frame = requestAnimationFrame(tick)
    }
    tick()
    return () => cancelAnimationFrame(frame)
  }, [key])
  return index
}

const SPOTLIGHT_GLIDE_MS = 300
/** Long enough for a new step's target to be measured, short enough that a target that left doesn't keep its hole. */
const SPOTLIGHT_HOLD_MS = 500

/** Holds the last hole briefly while a new step's target is measured, and glides only on a step change so it never trails a moving target. */
function useSpotlight(ring: Box | undefined, stepKey: string): { box: Box | null; gliding: boolean } {
  const [held, setHeld] = useState<Box | null>(null)
  const [glidingFor, setGlidingFor] = useState<string | null>(null)
  useEffect(() => {
    if (ring) {
      setHeld(ring)
      return
    }
    const timer = setTimeout(() => setHeld(null), SPOTLIGHT_HOLD_MS)
    return () => clearTimeout(timer)
  }, [ring])
  useEffect(() => {
    setGlidingFor(stepKey)
    const timer = setTimeout(() => setGlidingFor(null), SPOTLIGHT_GLIDE_MS)
    return () => clearTimeout(timer)
  }, [stepKey])
  return { box: ring ?? held, gliding: glidingFor === stepKey }
}

/** Beside the target when there is room, else below or above whatever it must not cover. */
function cardPosition(boxes: TargetBoxes | null, bounds: Box): { left: number; top?: number; bottom?: number } {
  if (!boxes) {
    return { left: bounds.left + 8, top: bounds.top + 56 }
  }
  const { ring, avoid } = boxes
  if (avoid.right + CARD_GAP + CARD_WIDTH <= bounds.right - 8) {
    return { left: avoid.right + CARD_GAP, top: Math.max(bounds.top + 8, ring.top - 12) }
  }
  const left = Math.min(
    Math.max(bounds.left + 8, (ring.left + ring.right) / 2 - CARD_WIDTH / 2),
    bounds.right - CARD_WIDTH - 8,
  )
  return avoid.bottom + CARD_GAP + 140 < bounds.bottom
    ? { left, top: avoid.bottom + CARD_GAP }
    : { left, bottom: window.innerHeight - avoid.top + CARD_GAP }
}

/** The sidepanel in the full-screen overlay, the whole window elsewhere. */
function surfaceBounds(): Box {
  const panel = document.querySelector('[data-sidepanel-content]')?.getBoundingClientRect()
  return panel ?? { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight }
}

/** True when a scrolling ancestor hides the element, like a skill further down the @ menu's list. */
function isClippedByScroller(element: Element): boolean {
  const rect = element.getBoundingClientRect()
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    if (parent.scrollHeight <= parent.clientHeight || getComputedStyle(parent).overflowY === 'visible') continue
    const view = parent.getBoundingClientRect()
    if (rect.top < view.top || rect.bottom > view.bottom) return true
  }
  return false
}

interface MissionCoachMarkProps {
  surface: TourSurface
  /** Runs in every window as a mission completes or is skipped, wherever its last step was. */
  onMissionComplete?: (mission: string, skipped: boolean) => void
  /** Shown on the finished card instead of auto-closing it, so the user can stay on the page they reached. */
  onBackToTour?: () => void
  /** The overlay's sidepanel is open; minimizing it ends a mission whose step is drawn there. */
  panelOpen?: boolean
}

/** Mounted once per window; each window draws only the steps whose target lives in it. */
export function MissionCoachMark(props: MissionCoachMarkProps) {
  // Nothing polls the page until a mission starts, and only the flagged tour starts one.
  const { mission } = useAtomValue(activeMissionAtom)
  return mission ? <ActiveMissionCoachMark {...props} /> : null
}

function ActiveMissionCoachMark({ surface, onMissionComplete, onBackToTour, panelOpen }: MissionCoachMarkProps) {
  const [mission, setMission] = useAtom(activeMissionAtom)
  const destination = findMission(mission.mission) ? mission.mission : null
  const { params } = mission
  const path = useMemo(() => findMission(destination)?.path(params) ?? [], [destination, params])
  const complete = destination !== null && mission.step >= path.length
  const step = complete ? undefined : path[mission.step]
  const owned = complete ? path[path.length - 1]?.surface === surface : step?.surface === surface
  const target = owned && step ? step.target : null
  const boxes = useTargetBoxes(target)
  const recovery = target && !boxes ? step?.recoverVia : undefined
  const recoveryBoxes = useTargetBoxes(recovery?.target ?? null)
  const laterTargets = useMemo(
    () =>
      step?.skipWhenLaterShows
        ? path.map((later, index) =>
            index > mission.step && later.surface === surface && !later.neverSkippedTo ? later.target : null,
          )
        : [],
    [step, path, mission.step, surface],
  )
  const furthestShowing = useFurthestShowing(laterTargets)
  useEffect(() => {
    if (furthestShowing >= 0) {
      setMission((prev) => (prev.step < furthestShowing ? { ...prev, step: furthestShowing } : prev))
    }
  }, [furthestShowing, setMission])

  // Every advance names the step it finishes, so two triggers for one step can't skip the next.
  const stepIndex = mission.step
  const advanceFrom = useCallback(
    (from: number) => setMission((prev) => (prev.step === from ? { ...prev, step: from + 1 } : prev)),
    [setMission],
  )

  const arrives = Boolean(step?.completeOnArrive)
  const arrived = arrives && boxes !== null && boxes.inSight
  useEffect(() => {
    if (arrived) {
      advanceFrom(stepIndex)
    }
  }, [arrived, stepIndex, advanceFrom])

  useEffect(() => {
    if (!target || arrives) {
      return
    }
    // Capture-phase pointerdown, because menus act and close before a click reaches the document.
    const advanceOnTarget = (event: PointerEvent) => {
      const element = document.querySelector(targetSelector(target))
      if (element && event.target instanceof Node && element.contains(event.target)) {
        advanceFrom(stepIndex)
      }
    }
    document.addEventListener('pointerdown', advanceOnTarget, true)
    return () => document.removeEventListener('pointerdown', advanceOnTarget, true)
  }, [target, arrives, stepIndex, advanceFrom])

  useEffect(() => {
    if (owned && panelOpen === false) {
      if (!complete) {
        trackTour('mission closed', { mission: mission.mission ?? '', step: stepIndex, reason: 'panel minimized' })
      }
      setMission(NO_MISSION)
    }
  }, [owned, complete, panelOpen, mission.mission, stepIndex, setMission])

  // Minimizing is keyed by step, so any advance brings the card back.
  const [minimizedAt, setMinimizedAt] = useState<number | null>(null)
  const minimized = minimizedAt === stepIndex
  const firstStep = owned && !complete && stepIndex === 0 && !minimized ? step : undefined
  useEffect(() => {
    if (!firstStep) {
      return
    }
    // Doing something else before the first step means they want the app, not the tour, for now.
    const minimize = () => {
      trackTour('mission minimized', { mission: mission.mission ?? '' })
      setMinimizedAt(0)
    }
    const allowed = [firstStep.target, firstStep.recoverVia?.target].flatMap((t) => (t ? [targetSelector(t)] : []))
    const onPointerDown = (event: PointerEvent) => {
      const node = event.target instanceof Element ? event.target : null
      if (!node?.closest('[data-tour-card]') && !allowed.some((selector) => node?.closest(selector))) {
        minimize()
      }
    }
    // `@` opens the skills menu, which is the first step itself.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.length === 1 && event.key !== '@' && !event.metaKey && !event.ctrlKey) {
        minimize()
      }
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKeyDown, true)
    }
  }, [firstStep, mission.mission])

  // Retried because a settings tab still animating in throws the first scroll off.
  const scrollRef = useRef<{ target: string | null; attempts: number; at: number }>({
    target: null,
    attempts: 0,
    at: 0,
  })
  useEffect(() => {
    if (!target || !boxes) {
      return
    }
    const element = document.querySelector(targetSelector(target))
    const bounds = surfaceBounds()
    const offscreen =
      boxes.ring.top < bounds.top ||
      boxes.ring.bottom > Math.min(bounds.bottom, window.innerHeight) ||
      (element !== null && isClippedByScroller(element))
    const tracker = scrollRef.current
    if (tracker.target !== target) {
      scrollRef.current = { target, attempts: 0, at: 0 }
    }
    const now = Date.now()
    if (
      !offscreen ||
      scrollRef.current.attempts >= MAX_SCROLL_ATTEMPTS ||
      now - scrollRef.current.at < SCROLL_RETRY_MS
    ) {
      return
    }
    scrollRef.current = { target, attempts: scrollRef.current.attempts + 1, at: now }
    element?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [target, boxes])

  // Some targets only show on hover, like a section's View all; keep the one being circled visible.
  const targetShows = boxes !== null
  useEffect(() => {
    const element = target && targetShows ? document.querySelector<HTMLElement>(targetSelector(target)) : null
    if (!element) {
      return
    }
    const previous = element.style.opacity
    element.style.opacity = '1'
    return () => {
      element.style.opacity = previous
    }
  }, [target, targetShows])

  const skippable = Boolean(target && step?.optional && !boxes)
  useEffect(() => {
    if (!skippable) {
      return
    }
    const timer = setTimeout(() => advanceFrom(stepIndex), OPTIONAL_STEP_WAIT_MS)
    return () => clearTimeout(timer)
  }, [skippable, stepIndex, advanceFrom])

  const skipped = Boolean(mission.skipped)
  const holdsForBack = Boolean(onBackToTour) && !skipped
  // A skip may come from a window that doesn't own the last step, so the always-mounted overlay clears it.
  const clearsHere = skipped ? Boolean(onMissionComplete) : owned && !holdsForBack
  useEffect(() => {
    if (!complete || !destination || !clearsHere) {
      return
    }
    const timer = setTimeout(() => setMission(NO_MISSION), skipped ? 0 : COMPLETE_HOLD_MS)
    return () => clearTimeout(timer)
  }, [complete, destination, clearsHere, skipped, setMission])

  useEffect(() => {
    if (complete && destination) {
      onMissionComplete?.(destination, skipped)
    }
  }, [complete, destination, skipped, onMissionComplete])

  const shown = boxes ?? recoveryBoxes
  const spotlight = useSpotlight(shown?.ring, `${destination}-${stepIndex}`)

  if (!owned || (complete && skipped)) {
    return null
  }

  const endMission = () => {
    trackTour('mission closed', { mission: mission.mission ?? '', step: mission.step })
    setMission(NO_MISSION)
  }
  // Checks the stop off like finishing it, so a skipped stop can't hold the round open.
  const skipMission = () => setMission((prev) => ({ ...prev, step: path.length, skipped: true }))
  const backToTour = () => {
    onBackToTour?.()
    setMission(NO_MISSION)
  }

  if (minimized) {
    return createPortal(
      <div className="fixed z-[9999]" style={cardPosition(null, surfaceBounds())} data-tour-card>
        <Interactive>
          <div className={`${CARD_SURFACE} flex max-w-[220px] items-center gap-1.5 rounded-full py-1 pl-2.5 pr-1.5`}>
            <Button
              variant="ghost"
              size="sm"
              aria-label="Resume mission"
              className="min-w-0"
              left={<Flag />}
              right={<Maximize2 className="text-foreground-placeholder" />}
              onClick={() => {
                trackTour('mission restored', { mission: mission.mission ?? '' })
                setMinimizedAt(null)
              }}
            >
              <span className="truncate">{mission.title}</span>
            </Button>
            <Tooltip>
              <Tooltip.Trigger
                render={
                  <Button variant="ghost" size="xs" shape="square" aria-label="End mission" onClick={endMission}>
                    <X />
                  </Button>
                }
              />
              <Tooltip.Content>End mission</Tooltip.Content>
            </Tooltip>
          </div>
        </Interactive>
      </div>,
      document.body,
    )
  }

  const hint = !boxes && recoveryBoxes ? recovery?.hint : step?.hint
  const card = cardPosition(shown, surfaceBounds())

  return createPortal(
    <>
      {/* Click-through, unlike the meeting tour's: a mission advances when the user clicks the real target. */}
      {spotlight.box && (
        <div
          aria-hidden
          className={twMerge(
            'bg-black-8 pointer-events-none fixed inset-0 z-[9998]',
            spotlight.gliding && 'transition-[clip-path] duration-300 ease-out motion-reduce:transition-none',
          )}
          style={{ clipPath: spotlightPath(spotlight.box) }}
        />
      )}
      <AnimatePresence>
        <motion.div
          key={`${destination}-${mission.step}`}
          data-tour-card
          className="fixed z-[9999]"
          style={{ ...card, width: CARD_WIDTH }}
          variants={CARD_VARIANTS}
          initial="hidden"
          animate="shown"
          exit="hidden"
          transition={CARD_TRANSITION}
        >
          <Interactive>
            <div
              role="dialog"
              aria-label={mission.title}
              className={`${CARD_SURFACE} relative flex flex-col gap-2 overflow-hidden rounded-2xl px-4 pb-4 pt-3`}
            >
              <h3 className="text-foreground relative my-0 pr-12 text-sm font-semibold">{mission.title}</h3>
              {!complete && (
                <span className="text-foreground-placeholder absolute right-4 top-3.5 text-xs font-medium tabular-nums">
                  {mission.step + 1} of {path.length}
                </span>
              )}
              <div className="relative flex items-end gap-3">
                <p className="text-foreground-secondary my-0 min-w-0 flex-1 text-sm">
                  {complete ? 'You made it. Mission complete.' : hint}
                  {!complete && !shown && ' — head back to Home if you don’t see it'}
                </p>
                {!complete && (
                  <Button size="sm" variant="ghost" className="-mb-0.5 -mr-2 shrink-0" onClick={skipMission}>
                    Skip
                  </Button>
                )}
              </div>
              {complete && holdsForBack && (
                <div className="flex items-center justify-end gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setMission(NO_MISSION)}>
                    Stay here
                  </Button>
                  <Button size="sm" variant="primary" onClick={backToTour}>
                    Back to tour
                  </Button>
                </div>
              )}
            </div>
          </Interactive>
        </motion.div>
      </AnimatePresence>
    </>,
    document.body,
  )
}
